import React, { useState, useRef, useContext, useEffect } from 'react';
import { ThemeContext } from 'styled-components';
import axios from 'axios';
import { BinCollectionProps, AddressOption } from './BinCollection.types';
import * as Styles from './BinCollection.styles';
import { useConfig } from '../../contexts/ConfigContext/ConfigContext';
import FormWithLine from '../FormWithLine/FormWithLine';
import FormButton from '../FormButton/FormButton';
import DropDownSelect from '../DropDownSelect/DropDownSelect';
import Heading from '../Heading/Heading';
import { ErrorText } from '../Input/Input.styles';
import { StyledTextInput } from '../TextInput/TextInput.styles';

/**
 * Returns the Bin Collection Checker form component to conduct search by postcode.
 */
const BinCollection: React.FunctionComponent<BinCollectionProps> = ({
  formError = false,
  graphqlApiEndpoint,
}) => {
  const config = useConfig();
  const resolvedApiUrl = graphqlApiEndpoint || config.binCollectionApiBaseUrl || config.nextPublicBinCollectionApiBaseUrl || '';
  const BinCollectionApiUrl = `${resolvedApiUrl}bin-collection-search/premiselist/`;
  const CalendarEventsApiUrl = `${resolvedApiUrl}bin-collection-search/calendarevents/`;
  const themeContext = useContext(ThemeContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setError] = useState<boolean>(formError);
  const [errorText, setErrorText] = useState<string>('');
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const seenTitles = useRef(new Set());
  const [noResultsFound, setNoResultsFound] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const enteredPostcode = e.target.elements.postcode.value;

    if (enteredPostcode === '') {
      setError(true);
      setErrorText('You need to enter a postcode');
    } else {
      setIsLoading(true);
      fetchAddresses(enteredPostcode);
    }
  };

  const formatUKPostcode = (postcode: string): string => {
    // Remove existing spaces and convert to uppercase
    const cleaned = postcode.replace(/\s/g, '').toUpperCase();
    // Add space before the last 3 characters
    const formatted = cleaned.slice(0, -3) + " " + cleaned.slice(-3);

    return formatted;
  }

  /**
   * Returns an array of address options based on the entered postcode.
   */
  const fetchAddresses = async (postcode) => {
    setCalendarEvents({});
    setError(false);
    setErrorText('');
    setIsLoading(true);
    postcode = formatUKPostcode(postcode);
    axios.get(`${BinCollectionApiUrl}${postcode}`)
      .then((response) => {
        setIsLoading(false);
        // Check if there are any addresses returned
        if (response.data.data.length === 0) {
          setError(true);
          setErrorText('No addresses found for the provided postcode.');
          return;
        }
        // Separate numeric and named addresses
        const numericAddresses = [];
        const namedAddresses = [];
        response.data.data.forEach(item => {
          const address = `${item.Address}`;
          if (/^\d/.test(address)) { // Address starts with a number
            numericAddresses.push({ value: item.UPRN, title: address });
          } else {
            namedAddresses.push({ value: item.UPRN, title: address });
          }
        });
        // Sort numeric addresses by the whole number
        numericAddresses.sort((a, b) => parseInt(a.title, 10) - parseInt(b.title, 10));
        // Sort named addresses alphabetically
        namedAddresses.sort((a, b) => a.title.localeCompare(b.title));
        // Combine the sorted arrays
        const sortedAddresses = numericAddresses.concat(namedAddresses);
        setAddressOptions(sortedAddresses);
      })
      .catch((error) => {
        setIsLoading(false);
        setError(true);
        console.error('API call error:', error);
        setErrorText('Error fetching addresses');
      });
    };

  const getFormattedDate = (daysToAdd = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysToAdd);
    return date.toISOString().split('T')[0];
  };

  const parseDate = (dateString) => {
    const timestamp = parseInt(dateString.match(/\d+/)[0], 10);
    return new Date(timestamp);
  }

  const groupByTitle = (data) => {
    const currentDate = new Date();
    return data.reduce((acc, item) => {
      const eventDate = parseDate(item.start);
      if (eventDate >= currentDate) {
        if (!acc[item.title]) {
          acc[item.title] = [];
        }
        acc[item.title].push(eventDate);
      }
      return acc;
    }, {});
  }

  const [calendarEvents, setCalendarEvents] = useState({});

  /**
   * Returns an array of calendar events based on selected address for next 42 days.
   */
  const handleAddressChange = (e) => {
    const selectedUPRN = e.target.value;
    
    if (selectedUPRN) {
      const startDate = getFormattedDate();
      const endDate = getFormattedDate(70);
      
      // Fetch calendar events for the selected UPRN
      axios.get(`${CalendarEventsApiUrl}${selectedUPRN}/${startDate}/${endDate}`)
      .then((response) => {
        // Filter the data based on the date range
        const filteredData = response.data.filter((event: any) => {
          const eventDate = parseDate(event.start);
          const eventDateString = eventDate.toISOString().split('T')[0];
          return eventDateString >= startDate && eventDateString <= endDate;
        });
        const groupedData = groupByTitle(filteredData);
        if (Object.keys(groupedData).length === 0) {
          setNoResultsFound(true);
        } else {
          setNoResultsFound(false);
        }
        setCalendarEvents(groupedData);
      })
      .catch((error) => {
        console.error('API call error:', error);
      });
    }
  };

  const resetForm = () => {
    setAddressOptions([]);
    setCalendarEvents({});
    setError(false);
    setErrorText('');
    seenTitles.current.clear();
    setNoResultsFound(false);
  };

  const formatTitle = (apiTitle: string) => {
    // console.log(apiTitle);
    const mappings = [
      {
          keyword: 'Refuse',
          replacement: 'General refuse bin',
          allowedValues: [
              'Refuse bin 1100', 'Refuse bin 140', 'Refuse bin 180',
              'Refuse bin 240', 'Refuse bin 360', 'Refuse bin 660',
              'Refuse Chamberlain', 'Refuse Palladin', 'Authorised bin (larger h/hold) 120',
              'Authorised bin (larger h/hold) 140', 'Authorised bin (larger h/hold) 180',
              'Authorised bin (offensive) 140', 'Authorised bin (offensive) 180', 'Authorised bin (offensive) 240',
          ]
      },
      {
          keyword: 'Recycling',
          replacement: 'Recycling bin',
          allowedValues: [
              'Recycling 1100', 'Recycling 140', 'Recycling 180',
              'Recycling 240', 'Recycling bin 1100'
          ]
      },
      {
          keyword: 'Garden',
          replacement: 'Garden bin',
          allowedValues: [
              'Garden 140', 'Garden 180', 'Garden 240'
          ]
      },
      {
          keyword: 'Food',
          replacement: 'Food caddy',
          allowedValues: [
            'Communal food bin (exterior) 180',
            'Food Caddy (exterior) 23',
            'Food Caddy (indoor) 5'
        ]
      },
    ];

    for (const mapping of mappings) {
      for (const value of mapping.allowedValues) {
        if (apiTitle.includes(value)) {
          return mapping.replacement;
        }
      }
    }

    return null;
  };

  useEffect(() => {
      seenTitles.current.clear();
  }, [calendarEvents]);


  return (
    <Styles.Container data-testid="BinCollection">
      <FormWithLine
        onSubmit={handleSubmit}
        isError={isError}
        lineColour={themeContext.theme_vars.colours.$grey_dark}
        hideLine
      >
        <Styles.FormContainer $isLoading={isLoading}>
        <Heading level={2} text="Bin collection checker" />
          <Styles.ErrorTextWrapper>
            {errorText && <ErrorText>{errorText}</ErrorText>}
          </Styles.ErrorTextWrapper>
          <Styles.FormInnerContainer> 
            <Styles.VisuallyHiddenLabel id="postcode-label" htmlFor="postcode-input">Enter a postcode</Styles.VisuallyHiddenLabel>
            <StyledTextInput
              type="text"
              placeholder="Enter a postcode"
              name="postcode"
              id="postcode-input"
              $isErrored={isError}
              aria-labelledby="postcode-label"
              autoComplete="postal-code"
            />
            <Styles.BinCollectionButtonStyles>
              <FormButton size="large" aria-label="Submit" text="Find" />
            </Styles.BinCollectionButtonStyles>
          </Styles.FormInnerContainer>
        </Styles.FormContainer>
      </FormWithLine>

      {addressOptions.length > 0 && (
        <Styles.DropDownSelectContainer>
          <DropDownSelect
            onChange={handleAddressChange}
            id="address"
            label="Select your address"
            options={[{ title: 'Select an address', value: '' }, ...addressOptions]}
          />
        </Styles.DropDownSelectContainer>
      )}

      {noResultsFound && <p>No results found.</p>}

      <div>
        {(() => {
          const order = ['General refuse bin', 'Recycling bin', 'Garden bin', 'Food caddy'];
          const uniqueTitles = Array.from(new Set(Object.keys(calendarEvents).map(apiTitle => formatTitle(apiTitle)).filter(title => title !== null)));

          const sortedTitles = uniqueTitles.sort((a, b) => order.indexOf(a) - order.indexOf(b));

          const noResultsFound = sortedTitles.length === 0;
          {noResultsFound && <p>No results found.</p>}

          return sortedTitles.map((title, index) => {
              const apiTitlesForThisType = Object.keys(calendarEvents).filter(apiTitle => formatTitle(apiTitle) === title);
              const dates = apiTitlesForThisType.flatMap(apiTitle => calendarEvents[apiTitle])
                .map(date => new Date(date).toISOString().split('T')[0])
                .filter((date, index, array) => array.indexOf(date) === index)
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
              return (
                  <React.Fragment key={`${title}-${index}`}>
                      <Heading level={3} text={title} />
                      {dates.map((date, index) => (
                        <p key={index}>
                          {new Date(date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      ))}
                  </React.Fragment>
              );
          });
        })()}
      </div>

      {Object.keys(calendarEvents).length > 0 && (
        <FormButton size="small" type="button" aria-label="Reset" text="Find a different address" onClick={resetForm} />
      )}

    </Styles.Container>
  );
};

export default BinCollection;
